import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  keyframes,
  Chip,
  Stack,
  IconButton,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { AutoAwesome, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useState } from "react";
import { toast } from "react-toastify";
import { getAiMode } from "../utils/util";

// Animation keyframes
const bounceIn = keyframes`
    0% { transform: scale(0); opacity: 0; }
    50% { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); opacity: 1; }
  `;
const shake = keyframes`
    0% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    50% { transform: translateX(5px); }
    75% { transform: translateX(-5px); }
    100% { transform: translateX(0); }
  `;
const fadeIn = keyframes`
    0% { opacity: 0; transform: translateY(-10px); }
    100% { opacity: 1; transform: translateY(0); }
  `;
const slideIn = keyframes`
    0% { opacity: 0; transform: translateX(20px); }
    100% { opacity: 1; transform: translateX(0); }
  `;

export const CommonAiOptions = ({ dialog, onClose }) => {
  const [selectedOption, setSelectedOption] = useState(getAiMode());
  const [carouselPage, setCarouselPage] = useState(0);
  const itemsPerPage = 4;

  // Get color based on type
  const getColor = () => {
    switch (dialog.type) {
      case "success":
        return "success.main";
      case "delete":
        return "error.main";
      case "fail":
        return "error.main";
      case "simple":
        if (dialog.subtype === "delete") return "warning.main";
        return "primary.main";
      default:
        return "primary.main";
    }
  };

  // Get icon based on type
  const getIcon = () => {
    const baseStyle = {
      fontSize: 24,
      marginRight: "16px",
      color: getColor(),
    };
    switch (dialog.type) {
      case "success":
        return (
          <CheckCircleOutlineIcon
            sx={{
              ...baseStyle,
              animation: `${bounceIn} 0.6s ease-out`,
            }}
          />
        );
      case "delete":
        return (
          <DeleteIcon
            sx={{
              ...baseStyle,
              animation: `${bounceIn} 0.6s ease-out`,
            }}
          />
        );
      case "fail":
        return (
          <ErrorOutlineIcon
            sx={{
              ...baseStyle,
              animation: `${shake} 0.5s ease-in-out`,
            }}
          />
        );
      case "simple":
        if (dialog.subtype === "delete") {
          return <WarningAmberIcon sx={baseStyle} />;
        }
        return <InfoOutlinedIcon sx={baseStyle} />;
      default:
        return <AutoAwesome sx={baseStyle} />;
    }
  };

  // Handle dialog close
  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      onClose();
    }
  };

  // Handle chip selection
  const handleChipClick = (option) => {
    setSelectedOption(option);
  };

  // Handle confirm action
  const onConfirm = () => {
    if (dialog.onConfirm) {
      if (dialog.requireInput && !selectedOption) {
        toast.error("Please select an option!");
        return;
      }
      dialog.onConfirm(selectedOption);
    }
  };

  // Calculate pagination for carousel
  const totalPages = Math.ceil((dialog.aiOptions?.length || 0) / itemsPerPage);
  const currentOptions = dialog.aiOptions?.slice(
    carouselPage * itemsPerPage,
    (carouselPage + 1) * itemsPerPage
  );

  const handleNextPage = () => {
    setCarouselPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const handlePrevPage = () => {
    setCarouselPage((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Dialog
      open={dialog.isAiOptions}
      onClose={handleClose}
      disableEscapeKeyDown={dialog.disableEscapeKeyDown}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minWidth: "400px",
          maxWidth: "500px",
          animation: `${fadeIn} 0.3s ease-out`,
        },
      }}
    >
      <DialogTitle sx={{ color: getColor() }}>
        {getIcon()}
        {dialog.title}
      </DialogTitle>
      <DialogContent>
        <Typography my={2} fontSize={"16px"}>
          {dialog.message}
        </Typography>
        {/* AI Options as Chips with Carousel */}
        {dialog.aiOptions && dialog.aiOptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={handlePrevPage}
                disabled={carouselPage === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ flex: 1 }}>
                {currentOptions?.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    onClick={() => handleChipClick(option)}
                    variant={selectedOption === option ? "filled" : "outlined"}
                    color={selectedOption === option ? "success" : "default"}
                    sx={{
                      mb: 1,
                      animation: `${slideIn} 0.3s ease-out`,
                      animationDelay: `${index * 0.05}s`,
                      maxWidth: "100%",
                      // Override hover to match selected state (green bg with white text)
                      "&:hover": {
                        backgroundColor: "success.main",
                        color: "success.contrastText",
                      },
                    }}
                  />
                ))}
              </Stack>
              <IconButton
                onClick={handleNextPage}
                disabled={carouselPage >= totalPages - 1}
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
            {/* Pagination indicators */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 0.5,
                  mt: 1,
                }}
              >
                {Array.from({ length: totalPages }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: i === carouselPage ? "primary.main" : "grey.400",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ pt: 1 }}>
        <Button autoFocus onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant={dialog.subtype === "delete" ? "contained" : "text"}
          autoFocus
          aria-label={dialog.buttonLabel || "Confirm dialog action"}
          sx={{
            color: dialog.subtype === "delete" ? "white" : getColor(),
            backgroundColor:
              dialog.subtype === "delete" ? getColor() : "transparent",
            borderRadius: 2,
            "&:hover": {
              backgroundColor:
                dialog.subtype === "delete" ? getColor() : `${getColor()}10`,
              opacity: 0.9,
            },
          }}
        >
          {dialog.buttonText || "OK"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
